import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, TrendingUp, Clock, Shield, Users, Package, ArrowRight, Star } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DealerProgramPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dealerInfo, setDealerInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Certified Dealer Application | NICE Grading';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join the NICE Grading Dealer Program for exclusive volume pricing, priority turnaround, and dedicated support. Boost your margins and grow your business.');
    }
    return () => {
      document.title = 'NICE Grading | Collectors First. Always NICE.';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'The most collector friendly grading service in the hobby. Powering the revolution one slab at a time.');
      }
    };
  }, []);

  useEffect(() => {
    const fetchDealerInfo = async () => {
      try {
        const response = await axios.get(`${API}/api/dealer/pricing-tiers`);
        setDealerInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch dealer info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDealerInfo();
  }, []);

  const handleApplyClick = () => {
    if (isAuthenticated) {
      navigate('/dealer/apply');
    } else {
      // Redirect to login with the dealer apply page as the return destination
      navigate('/login', { state: { from: { pathname: '/dealer/apply' } } });
    }
  };

  const benefits = [
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-600" />,
      title: "Exclusive Volume Pricing",
      description: "Unlock special dealer rates that increase your profit margins on every submission"
    },
    {
      icon: <Clock className="h-8 w-8 text-blue-600" />,
      title: "Priority Turnaround",
      description: "Get your cards graded faster with preferred processing for dealer accounts"
    },
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: "Dedicated Support",
      description: "Direct access to our dealer support team for questions and special requests"
    }
  ];

  const pricingTiers = [
    {
      name: "Dealer Standard",
      cardRange: "250-499 cards",
      highlight: "Great for getting started",
      features: ["Volume discount pricing", "Priority processing", "Dealer support access"]
    },
    {
      name: "Dealer Volume",
      cardRange: "500-999 cards",
      highlight: "Best value for growing dealers",
      features: ["Enhanced pricing tier", "Faster turnaround", "Dedicated account support"],
      popular: true
    },
    {
      name: "Dealer Premium",
      cardRange: "1,000+ cards",
      highlight: "Maximum savings",
      features: ["Lowest per-card pricing", "Fastest turnaround", "White-glove service"]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - Blueprint/Engineering Mat Style */}
      <section className="relative bg-[#002554] overflow-hidden">
        {/* Blueprint grid pattern with gradient lighting effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, 
                rgba(0, 53, 148, 0.4) 0%, 
                rgba(0, 37, 84, 0.1) 30%,
                rgba(0, 37, 84, 0.1) 70%,
                rgba(0, 53, 148, 0.3) 100%
              )
            `
          }}
        />
        
        {/* Tight blueprint grid - smaller squares */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 53, 148, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 53, 148, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '24px 24px'
          }}
        />
        
        {/* Secondary finer grid overlay */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 53, 148, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 53, 148, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '6px 6px'
          }}
        />

        {/* Gradient light sweep from top-left */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 30%, 
                rgba(0, 53, 148, 0.6) 0%, 
                transparent 60%
              )
            `
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Modern Grading For Modern Dealers
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Join our Certified Dealer Program to unlock exclusive volume pricing and priority turnaround times that help you grow your reseller business.
            </p>
            <Button 
              size="lg" 
              onClick={handleApplyClick}
              className="bg-white text-[#002554] hover:bg-gray-100 text-lg px-8 py-6"
              data-testid="apply-now-hero-btn"
            >
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Become a Certified Dealer?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our Certified Dealer Program is designed for modern dealers and streamers who understand how rapid turnaround times (and putting collectors-first) can grow your business more than any other option.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow w-full md:w-72">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Dealer Pricing Tiers</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The more you submit, the more you save. Our tiered pricing rewards your volume.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative ${tier.popular ? 'border-2 border-blue-600 shadow-xl' : 'border shadow-lg'}`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-3 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <CardDescription className="text-blue-600 font-semibold text-lg">
                    {tier.cardRange}
                  </CardDescription>
                  <p className="text-gray-500 text-sm mt-2">{tier.highlight}</p>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-3">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-gray-500 mt-8 text-sm">
            * Exact dealer pricing is provided upon approval. Pricing varies based on volume and commitment.
          </p>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Program Requirements</h2>
          </div>
          
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center">
                    <Package className="h-5 w-5 text-blue-600 mr-2" />
                    To Qualify
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Plan to submit 250+ cards per month
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Complete dealer application
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Agree to Dealer Terms & Conditions
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center">
                    <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
                    To Maintain Status
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Submit at least 250 cards per month
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Maintain active account in good standing
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      Comply with program terms
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h2>
          <p className="text-blue-100 mb-8 text-lg">
            Join the NICE Dealer Program today and start saving on every submission.
          </p>
          <Button 
            size="lg" 
            onClick={handleApplyClick}
            className="bg-white text-blue-900 hover:bg-gray-100"
            data-testid="apply-now-cta-btn"
          >
            Apply Now <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
